import React, { forwardRef } from 'react';

import { CloseFill, FunnelOutline } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { LinkButton, Stack, Typography } from '@lyearn/ui';
import { FilterOperators } from '@/components/Filter/types';

import { MenuActionButton } from '../../components/ActionButton';
import {
  AppliedFilterItemAppearance,
  AppliedFilterItemVariant,
} from '../../EntityFilterManager/types';
import { closeIconStyles } from '../../SortAndFilter/components/TagsFilterContainer/styles';
import { getButtonLabel } from '../../SortAndFilter/helpers';
import FilterLabelWithOperator from './FilterLabelWithOperator';

export interface EntityDefaultChildrenProps {
  className?: string;
  selectedValues?: string[];
  variant?: AppliedFilterItemVariant;
  appearance?: AppliedFilterItemAppearance;
  selectedOperator?: FilterOperators;
  defaultPlaceholder: string;
  StartIcon?: typeof CloseFill;
  onRemove?: () => void;
  id?: string;
  isNonEditable?: boolean;
  EntityChildrenComponent?: React.ForwardRefExoticComponent<
    EntityDefaultChildrenProps & React.RefAttributes<any>
  >;
}

const EntityDefaultChildren = forwardRef<any, EntityDefaultChildrenProps>((props, ref) => {
  const { EntityChildrenComponent, ...restProps } = props;

  if (EntityChildrenComponent) {
    return <EntityChildrenComponent ref={ref} {...restProps} />;
  }

  const {
    variant,
    selectedValues = [],
    selectedOperator,
    defaultPlaceholder,
    className,
    onRemove,
    appearance,
    id,
    isNonEditable,
    StartIcon,
    // popover btn passes extra props, aria-label, onClick, etc
    ...rest
  } = restProps;

  if (variant === 'fullWidth') {
    const FinalStartIcon = StartIcon ?? FunnelOutline;
    return (
      <Stack
        ref={ref}
        alignItems="center"
        className="h-40 truncate rounded-8 border border-solid border-interactive-secondary-hover pe-4 ps-12"
        justifyContent="between"
        {...rest}>
        <Stack className="truncate">
          <FinalStartIcon
            className="h-20 w-20 flex-shrink-0 me-8"
            fillColorToken="icon-placeholder"
          />
          <Typography className="truncate" variant="body-short-02">
            {defaultPlaceholder}
          </Typography>
          <Typography className="mx-6" variant="body-short-02">
            {selectedOperator?.label}
          </Typography>
          <Typography className="truncate" variant="body-short-02">
            {selectedValues[0]}
          </Typography>
          {selectedValues.length > 1 ? (
            <Typography variant="body-short-02">+{selectedValues.length - 1}</Typography>
          ) : null}
        </Stack>
        {onRemove ? (
          <LinkButton
            className="py-2 px-4"
            color={'internal'}
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}>
            <CloseFill />
          </LinkButton>
        ) : null}
      </Stack>
    );
  }

  return (
    <MenuActionButton
      ref={ref}
      appearance={appearance}
      className={cn({ 'pointer-events-none': isNonEditable }, className)}
      endIcon={
        isNonEditable ? (
          <></>
        ) : onRemove ? (
          <LinkButton
            color={'internal'}
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}>
            <CloseFill className={closeIconStyles} />
          </LinkButton>
        ) : null
      }
      id={id}
      isSelected={!selectedOperator}
      startIcon={StartIcon ? <StartIcon /> : null}
      {...rest}>
      {selectedOperator ? (
        <FilterLabelWithOperator
          entityLabel={defaultPlaceholder}
          operator={selectedOperator}
          value={selectedValues}
        />
      ) : (
        getButtonLabel(selectedValues, defaultPlaceholder)
      )}
    </MenuActionButton>
  );
});

EntityDefaultChildren.displayName = 'EntityDefaultChildren';

export default EntityDefaultChildren;
